/**
 * scrolltop1(2026-10-18) — 외부 유입 «첫 로드» 는 맨 위에서 시작한다(판정 순수 함수 · DOM 없음).
 *
 * 증상: iPhone 네이버 앱 인앱 브라우저에서 검색 결과(target=_self · 해시 없음)를 누르면 상세가 «최하단»(면책·푸터·mbar)으로 열렸다.
 * 판독: 운영 6 URL × 4 프로필(Playwright WebKit iPhone 13 + 네이버/카카오 인앱 UA · Safari · Chrome Android)에서
 *   우리 코드의 로드 시 scrollTo/scrollBy/scrollIntoView/focus/scrollTop= 호출 0 · 해시 0 · autofocus 0 · router 이동 0.
 *   → 스크롤을 옮기는 주체는 인앱 WebView(긴 검색 결과 화면의 오프셋을 새 문서에 넘기거나 위치를 복원 — 새 문서 높이로 잘려 «최하단»).
 *   우리가 고칠 수 있는 곳은 «받는 쪽» 뿐이라 루트에 작은 가드를 둔다(components/common/FirstLoadScrollTop).
 *
 * 규칙(instr/cl/scrolltop1 §수리 원칙):
 *   · navigate(+prerender) 첫 로드 · 해시 없음 → 하이드레이션 뒤 scrollY>0 이고 사용자 입력 전이면 맨 위로.
 *   · reload · back_forward 는 브라우저 복원에 맡긴다(건드리지 않는다).
 *   · 해시 진입(#lead-form 등)은 의도대로 둔다.
 *   · 사용자가 한 번이라도 만졌으면(터치·포인터·휠·키) 절대 끌어올리지 않는다 — 하이드레이션 «전» 입력은 <head> 인라인 스크립트가 잡는다.
 */

/** <head> 인라인 스크립트가 첫 사용자 입력 때 1 로 세운다(하이드레이션 전 입력 포함). */
export const USER_ACTED_FLAG = '__kdUA' as const;

/** 가드가 감시하는 사용자 입력. touchstart 가 iOS 의 손가락 스크롤 시작을 잡는다. */
export const USER_INPUT_EVENTS = ['touchstart', 'pointerdown', 'mousedown', 'wheel', 'keydown'] as const;

/** load 뒤 이만큼 더 지켜본다(WebView 의 늦은 위치 적용). */
export const GUARD_AFTER_LOAD_MS = 2500;
/** 마운트 뒤 최대 감시 시간(load 가 늦거나 안 와도 여기서 끝). */
export const GUARD_MAX_MS = 10000;

export type NavType = 'navigate' | 'reload' | 'back_forward' | 'prerender' | null;

/** Navigation Timing L2 → 없으면 L1(performance.navigation.type 0/1/2) → 모르면 null(가드 안 함). */
export function navTypeOf(perf: { getEntriesByType?: (t: string) => unknown[]; navigation?: { type?: number } } | undefined | null): NavType {
  try {
    const e = perf?.getEntriesByType?.('navigation')?.[0] as { type?: string } | undefined;
    const t = e?.type;
    if (t === 'navigate' || t === 'reload' || t === 'back_forward' || t === 'prerender') return t;
  } catch { /* 구형 브라우저 */ }
  const l1 = perf?.navigation?.type;
  if (l1 === 0) return 'navigate';
  if (l1 === 1) return 'reload';
  if (l1 === 2) return 'back_forward';
  return null;
}

/** 이 문서에서 가드를 켤지. 해시가 있거나 reload/back_forward/모름이면 끈다. */
export function shouldArmFirstLoadTop(nav: NavType, hash: string | null | undefined): boolean {
  if (hash && hash !== '#') return false;
  return nav === 'navigate' || nav === 'prerender';
}

/** 지금 맨 위로 돌릴지(가드가 켜진 동안 매 검사). */
export function shouldResetNow(s: { scrollY: number; userActed: boolean; hash: string | null | undefined; sameUrl: boolean }): boolean {
  if (s.userActed) return false;
  if (!s.sameUrl) return false;
  if (s.hash && s.hash !== '#') return false;
  return s.scrollY > 0;
}

/**
 * 하이드레이션 전 사용자 입력 기록(<head> 인라인 · CSP 는 sha256 해시 — lib/csp/inline-scripts).
 * ⚠️ 본문을 바꾸면 해시가 달라진다(csp-inline-hash.test 가 새 값을 알려 준다). 방문자·페이지 무관 고정 문자열(클로킹 아님).
 */
export const FIRST_INPUT_CAPTURE_SCRIPT =
  "(function(){try{var w=window,o={capture:true,passive:true},ev=['touchstart','pointerdown','mousedown','wheel','keydown'];w.__kdUA=0;var f=function(){w.__kdUA=1;for(var i=0;i<ev.length;i++)w.removeEventListener(ev[i],f,o)};for(var i=0;i<ev.length;i++)w.addEventListener(ev[i],f,o)}catch(e){}})();";
